import type { MonthlyShiftSlotData } from '../../domain/dailyShiftSlot';
import type {
  DailyShiftSlotRepository,
  MonthlyShiftSlotFilter,
  UpdateShiftSlotData,
  UpdateStatusData,
  SendVacancyNotificationData,
  SendUrgentNotificationData,
  ImportResult,
  ExportResult,
} from '../../application/dailyShiftSlot/ports/DailyShiftSlotRepository';
import { dailyShiftSlotApi } from '../api/dailyShiftSlotApi';

/**
 * DailyShiftSlotRepository 実装（Clean Architecture: Infrastructure Layer）
 */
export class DailyShiftSlotRepositoryImpl implements DailyShiftSlotRepository {
  /**
   * 月次シフト枠データ取得
   * @param filter MonthlyShiftSlotFilter 検索フィルター（年月）
   * @returns Promise<MonthlyShiftSlotData> 月次シフト枠データ
   */
  async getByMonth(filter: MonthlyShiftSlotFilter): Promise<MonthlyShiftSlotData> {
    return await dailyShiftSlotApi.getByMonth(filter.year, filter.month);
  }

  /**
   * シフト枠一括更新
   * @param slots UpdateShiftSlotData[] 更新データ配列
   * @returns Promise<void>
   */
  async updateSlots(slots: UpdateShiftSlotData[]): Promise<void> {
    await dailyShiftSlotApi.updateSlots(
      slots.map(slot => ({
        id: slot.id,
        recruitmentSlots: slot.recruitmentSlots,
        urgentSlots: slot.urgentSlots,
      }))
    );
  }

  /**
   * ステータス更新
   * @param data UpdateStatusData ステータス更新データ
   * @returns Promise<void>
   */
  async updateStatus(data: UpdateStatusData): Promise<void> {
    await dailyShiftSlotApi.updateStatus({
      year: data.year,
      month: data.month,
      status: data.status,
      recruitmentStartAt: data.recruitmentStartAt,
      deadlineAt: data.deadlineAt,
    });
  }

  /**
   * 空き枠紹介通知送信
   * @param data SendVacancyNotificationData 通知データ
   * @returns Promise<void>
   */
  async sendVacancyNotification(data: SendVacancyNotificationData): Promise<void> {
    await dailyShiftSlotApi.sendVacancyNotification({
      year: data.year,
      month: data.month,
      doctorIds: data.doctorIds,
    });
  }

  /**
   * 急募枠通知送信
   * @param data SendUrgentNotificationData 通知データ
   * @returns Promise<void>
   */
  async sendUrgentNotification(data: SendUrgentNotificationData): Promise<void> {
    await dailyShiftSlotApi.sendUrgentNotification({
      year: data.year,
      month: data.month,
      doctorIds: data.doctorIds,
    });
  }

  /**
   * CSVインポート
   * @param year number 年
   * @param month number 月
   * @param csvData string CSVデータ
   * @returns Promise<ImportResult> インポート結果
   */
  async importCsv(year: number, month: number, csvData: string): Promise<ImportResult> {
    const result = await dailyShiftSlotApi.importCsv(year, month, csvData);
    return {
      success: result.success,
      importedCount: result.importedCount,
      errors: result.errors,
    };
  }

  /**
   * CSVエクスポート
   * @param year number 年
   * @param month number 月
   * @returns Promise<ExportResult> エクスポート結果
   */
  async exportCsv(year: number, month: number): Promise<ExportResult> {
    const result = await dailyShiftSlotApi.exportCsv(year, month);
    return {
      success: result.success,
      csvData: result.csvData,
      filename: result.filename,
    };
  }
}

/**
 * Singleton インスタンス
 */
export const dailyShiftSlotRepository = new DailyShiftSlotRepositoryImpl();
